import React, {useState} from 'react';

import {Box, Button, Divider, styled, Typography, useMediaQuery} from '@mui/material';
// eslint-disable-next-line import/no-cycle
import {useTranslation} from 'react-i18next';
import {useDispatch} from 'react-redux';

import theme from 'config/theme';
import useAnalytics, {ANALYTICS, EVENT_SOURCE, SUGGESTEDLIST_CHANNELS} from 'contexts/analytics-context';
import {useAppSelector} from 'hooks';
import {useSelectedItemsLocalStorage} from 'hooks/useSelectedItemsLocalStorage';
import ReactNative from 'services/react-native';
import {bulkUpdateBasketItemsPreviewAction, updatePreviewItemQuantityAction} from 'store/actions/basket-action';
import {AnalyticsData, ProductSectionTypes, ProductType} from 'types';
import {ProductSectionProps} from 'types';
import {findBasketItemByVariantId, roundOffDecimalQuantity} from 'utils';
import {filter1PItems, filter3PItems} from 'utils/helper';
import {getProductAvailability, getProductIdWithoutPrefix} from 'utils/product';
import FlexiBasketConfirmModal from 'views/basket-modals/FlexiBasketConfirmModal';
import BasketProductV2 from 'views/basket-product/ProductV2';

import {StyledSectionContentContainer} from './styles';
import {MAKNET} from '../../constants/index';

export type BasketProps = {
  open: boolean;
  toggleBasketDrawer: () => void;
};

export const StyledSectionWrapV2 = styled(Box)<{isReactNativeView?: boolean}>(({theme, isReactNativeView}) => ({
  display: 'flex',
  width: '100%',
  flexDirection: 'row',
  position: 'relative',
  margin: isReactNativeView ? '8px' : '20px',
  maxWidth: '-webkit-fill-available',
  borderRadius: '12px',
  backgroundColor: theme.palette.common.white,
  '&.multiple': {
    [theme.breakpoints.down('md')]: {
      marginBottom: isReactNativeView ? theme.spacing(1) : theme.spacing(2)
    }
  }
}));

export const StyledUnavailableSectionHeader = styled(Box)<{isReactNativeView?: boolean}>(
  ({theme, isReactNativeView}) => ({
    borderRadius: '12px 12px 0px 0px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: theme.palette.grey[200],
    padding: isReactNativeView ? '0px 12px 0px 12px' : '0px 16px 0px 16px'
  })
);

export const UnavailableSection: React.FC<ProductSectionProps> = ({mode, data, ...section}) => {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const analytics = useAnalytics();

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const {currentStore, defaultMembershipCard} = useAppSelector((state) => state?.app);
  const isMobileView = useMediaQuery(theme.breakpoints.only('mobile'));
  const selectedItemsLocalStorage = useSelectedItemsLocalStorage();
  const selectedItems = selectedItemsLocalStorage.get();
  const currentStoreCodes = currentStore?.storeCodes;
  const [basketItem] = data;
  const sellerType = basketItem?.productDetails?.sellerDetails?.fulfilledByPlatform;
  const productType =
    sellerType?.toLowerCase() === MAKNET.toLowerCase() ? ProductType.PRODUCT_1P : ProductType.PRODUCT_3P;
  const idToken = useAppSelector((state) => state?.auth?.idToken);
  const {basket} = useAppSelector((state) => state?.basket);
  const isReactNativeWebView = ReactNative.isReactNativeWebView();
  const isMobileScreen = isReactNativeWebView || isMobileView;

  const deleteUnavailableItems = async () => {
    try {
      const unavailableIds: string[] = [];
      const payload = data.map((itm) => {
        unavailableIds.push(itm.productId);
        return {
          quantity: 0,
          productId: itm.productId
        };
      });
      dispatch(bulkUpdateBasketItemsPreviewAction(payload));
      const filteredSelectedItems = selectedItems?.filter((itm: string) => !unavailableIds.includes(itm));
      selectedItemsLocalStorage.set(filteredSelectedItems);
    } catch (error) {
      console.log('error', error);
    }
  };

  const updateItemQuantity = async (
    quantity: number,
    variantId: string,
    analyticsData?: AnalyticsData
  ): Promise<void> => {
    const basketItem = findBasketItemByVariantId(data, variantId);
    try {
      dispatch(
        updatePreviewItemQuantityAction({
          productId: getProductIdWithoutPrefix(basketItem?.productId || ''),
          quantity: roundOffDecimalQuantity(quantity),
          idToken,
          variantId
        })
      );
      if (analyticsData) {
        analytics.trackEvent(ANALYTICS.UPDATE_BASKET_ITEM, {
          sourceEvent: analyticsData.sourceEvent,
          triggerType: analyticsData.triggerType,
          basketId: basket?.id || 0,
          itemsCount: basket?.basketItems.length || 0,
          productId: analyticsData.productId || '',
          qty: analyticsData.qty,
          productPrice: analyticsData.productPrice || '',
          priceBeforeDiscount: basket?.prices?.totalOriginalPrice || 0,
          total: basket?.prices?.totalFinalPriceToPay || 0,
          discount: basket?.prices?.roundedDiscountAmount || 0,
          delivery: basket?.prices?.shippingFee || 0,
          storeId: basket?.storeCode || '',
          channel: SUGGESTEDLIST_CHANNELS.MAKRO_PRO,
          segment_source_name: isReactNativeWebView ? EVENT_SOURCE.APP : EVENT_SOURCE.WEB,
          basketSize: basket?.basketItems?.length || 0,
          onePCount: filter1PItems(basket?.basketItems || []).length || 0,
          threePCount: filter3PItems(basket?.basketItems || []).length || 0
        });
      }
    } catch (error: any) {
      throw new Error(error.description);
    }
  };

  return (
    <>
      <StyledSectionWrapV2 className={`${mode} ${'v2'}`} isReactNativeView={isMobileScreen}>
        <StyledSectionContentContainer>
          <StyledUnavailableSectionHeader isReactNativeView={isMobileScreen}>
            <Typography>
              <b>{t('label.notReadyForSale')}</b>
            </Typography>
            <Button onClick={() => setShowConfirmModal(true)}>{t('flexiBasket.deleteAll')}</Button>
          </StyledUnavailableSectionHeader>

          {data.map((itm, key) => {
            let displayPrice = itm.productDetails.displayPrice;
            const [variant] = itm.productDetails.variants;
            if (itm.promotedPrice) {
              displayPrice = itm.promotedPrice;
            } else {
              displayPrice = variant.price;
            }

            const {status: productStatus} = getProductAvailability(itm.productDetails, currentStoreCodes || [], {
              alcoholExpiryDate: defaultMembershipCard?.alcoholExpiryDate
            });
            const isProductOnProhibitedPeriod = section.type === ProductSectionTypes.ALCOHOL_PROHIBITED_PERIOD;

            return (
              <>
                <Box key={itm.id} pl={isMobileScreen ? 1.5 : 2} pr={isMobileScreen ? 1.5 : 2}>
                  <BasketProductV2
                    loading={false}
                    productUnavailableText={
                      isProductOnProhibitedPeriod ? t(`label.restricted`) : t(`label.${productStatus}`)
                    }
                    title={itm.productDetails.title}
                    coverImageURL={itm.productDetails.imageUrls[0]}
                    onQuantityChange={(qty: number, analyticsData?: AnalyticsData) =>
                      updateItemQuantity(qty, itm?.variantId || '', analyticsData)
                    }
                    weight={itm.productDetails?.size || ''}
                    productId={itm.productId}
                    basketItem={itm}
                    productType={productType}
                    displayPrice={displayPrice}
                  />
                </Box>
                {data.length - 1 === key ? null : <Divider />}
              </>
            );
          })}
        </StyledSectionContentContainer>
      </StyledSectionWrapV2>
      <FlexiBasketConfirmModal
        onConfirm={() => {
          deleteUnavailableItems();
          setShowConfirmModal(false);
        }}
        setShowConfirmModal={setShowConfirmModal}
        open={showConfirmModal}
        message={t('flexiBasket.deleteUnavailableModalInfoText', {
          numberOfItems: data.length
        })}
        isReactNativeWebView={isReactNativeWebView || !!isMobileView}
      />
    </>
  );
};

UnavailableSection.displayName = 'UnavailableSection';
